import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../utils/api";

// Initial state for passes
const initialState = {
  passes: [],
  currentPass: null,
  loading: false,
  error: null,
};

// Async thunk to get passes
export const getPasses = createAsyncThunk(
  "passes/getPasses",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/passes");
      return response.data.data;
    } catch (error) {
      console.error("API Error:", error.response?.data);
      return rejectWithValue(error.response?.data || "Failed to fetch passes");
    }
  }
);

export const checkInPass = createAsyncThunk(
  "passes/checkInPass",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.post(`/passes/check-in/${id}`);
      return response.data.data; // Assuming the API returns updated pass data
    } catch (error) {
      console.error("API Error:", error.response?.data);
      return rejectWithValue(error.response?.data || "Failed to check in pass");
    }
  }
);

// Async thunk for check-out
export const checkOutPass = createAsyncThunk(
  "passes/checkOutPass",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.post(`/passes/check-out/${id}`);
      return response.data.data; // Assuming the API returns updated pass data
    } catch (error) {
      console.error("API Error:", error.response?.data);
      return rejectWithValue(
        error.response?.data || "Failed to check out pass"
      );
    }
  }
);

export const getSinglePass = createAsyncThunk(
  "passes/getSinglePass",
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/passes/${id}`);
      return response.data.data; // Return the single pass data
    } catch (error) {
      console.error("API Error:", error.response?.data);
      return rejectWithValue(error.response?.data || "Failed to fetch pass");
    }
  }
);

// Async thunk to add a pass
export const addPass = createAsyncThunk(
  "passes/addPass",
  async (passData, { rejectWithValue }) => {
    try {
      const response = await api.post("/passes", passData, {
        headers: { "Content-Type": "application/json" },
      });
      return response.data.data;
    } catch (error) {
      console.error("API Error:", error.response?.data);
      return rejectWithValue(error.response?.data || "Failed to add pass");
    }
  }
);

// Async thunk to update a pass
export const updatePass = createAsyncThunk(
  "passes/updatePass",
  async ({ id, passData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/passes/${id}`, passData, {
        headers: { "Content-Type": "application/json" },
      });
      return response.data.data;
    } catch (error) {
      console.error("API Error:", error.response?.data);
      return rejectWithValue(error.response?.data || "Failed to update pass");
    }
  }
);

// Async thunk to delete a pass
export const deletePass = createAsyncThunk(
  "passes/deletePass",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/passes/${id}`);
      return id;
    } catch (error) {
      console.error("API Error:", error.response?.data);
      return rejectWithValue(error.response?.data || "Failed to delete pass");
    }
  }
);

// Create the pass slice
const passSlice = createSlice({
  name: "passes",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Handle getPasses
      .addCase(getPasses.pending, (state) => {
        state.loading = true;
      })
      .addCase(getPasses.fulfilled, (state, action) => {
        state.loading = false;
        state.passes = action.payload;
      })
      .addCase(getPasses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getSinglePass.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSinglePass.fulfilled, (state, action) => {
        state.loading = false;
        state.currentPass = action.payload; // Save the single pass to currentPass
      })
      .addCase(getSinglePass.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle addPass
      .addCase(addPass.pending, (state) => {
        state.loading = true;
      })
      .addCase(addPass.fulfilled, (state, action) => {
        state.loading = false;
        state.passes.push(action.payload);
      })
      .addCase(addPass.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle updatePass
      .addCase(updatePass.pending, (state) => {
        state.loading = true;
      })
      .addCase(updatePass.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.passes.findIndex(
          (pass) => pass.id === action.payload.id
        );
        if (index !== -1) {
          state.passes[index] = action.payload;
        }
      })
      .addCase(updatePass.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle deletePass
      .addCase(deletePass.pending, (state) => {
        state.loading = true;
      })
      .addCase(deletePass.fulfilled, (state, action) => {
        state.loading = false;
        state.passes = state.passes.filter(
          (pass) => pass.id !== action.payload
        );
      })
      .addCase(deletePass.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(checkInPass.pending, (state) => {
        state.loading = true;
      })
      .addCase(checkInPass.fulfilled, (state, action) => {
        state.loading = false;
        // Optionally, update the specific pass in the state
        const updatedPass = action.payload;
        const index = state.passes.findIndex(
          (pass) => pass.id === updatedPass.id
        );
        if (index !== -1) {
          state.passes[index] = updatedPass; // Update the pass state with the checked-in status
        }
      })
      .addCase(checkInPass.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle checkOutPass
      .addCase(checkOutPass.pending, (state) => {
        state.loading = true;
      })
      .addCase(checkOutPass.fulfilled, (state, action) => {
        state.loading = false;
        // Optionally, update the specific pass in the state
        const updatedPass = action.payload;
        const index = state.passes.findIndex(
          (pass) => pass.id === updatedPass.id
        );
        if (index !== -1) {
          state.passes[index] = updatedPass; // Update the pass state with the checked-out status
        }
      })
      .addCase(checkOutPass.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

// Export the reducer and actions
export const passReducer = passSlice.reducer;
export const passActions = {
  getPasses,
  addPass,
  updatePass,
  deletePass,
  getSinglePass,
  checkInPass,
  checkOutPass,
};
